import React, { forwardRef, useRef } from 'react';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import Draggable from 'react-draggable';
import { IMaskInput, IMask } from 'react-imask';
import { useTranslation } from 'react-i18next';
import { nameValidation } from 'utils/Validations';

type Mode = 'add' | 'edit';

type PaycodeOption = {
    paycode_id: number | string;
    name: string;
    [key: string]: unknown;
};

type ExceptionRuleFormValues = {
    name: string;
    description: string;
    not_planning: boolean;
    long_interval: string;
    shortened_shift: string;
    in_punch_very_early: string;
    in_punch_early: string;
    in_punch_late: string;
    out_punch_early: string;
    out_punch_late: string;
    out_punch_very_late: string;
};

type ExceptionRuleFormDialogProps = {
    open: boolean;
    mode: Mode;
    loading: boolean;
    values: ExceptionRuleFormValues;
    paycodeOptions: PaycodeOption[];
    inPaycodesDuration: PaycodeOption | null;
    outPaycodesDuration: PaycodeOption | null;
    nameExists: boolean;
    nameExistsTextKey: string;
    validateInPunchVeryEarly: (value: string) => boolean;
    validateOutPunchVeryLate: (value: string) => boolean;
    timeValidation: (value: string) => boolean;
    submitDisabled: boolean;
    showUnsavedAlert: boolean;
    onNameChange: (name: string) => void;
    onDescriptionChange: (description: string) => void;
    onNotPlanningChange: (checked: boolean) => void;
    onTimeFieldChange: (field: keyof ExceptionRuleFormValues, value: string) => void;
    onInPaycodeChange: (value: PaycodeOption | null) => void;
    onOutPaycodeChange: (value: PaycodeOption | null) => void;
    onRequestClose: () => void;
    onDismissUnsavedAlert: () => void;
    onDiscardChanges: () => void;
    onSubmit: () => void;
};

const PaperComponent = (props: React.ComponentProps<typeof Paper>) => {
    const nodeRef = useRef<HTMLDivElement | null>(null);

    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
};

const TextMaskCustom = forwardRef<HTMLInputElement, any>((props, ref) => {
    return (
        <IMaskInput
            {...props}
            mask="HH:MM"
            definitions={{
                H: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 2,
                },
                M: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 5,
                },
            }}
            blocks={{
                HH: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 24,
                    maxLength: 2,
                },
                MM: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 59,
                    maxLength: 2,
                },
            }}
            prepare={(value: string) => {
                const parts = value.split(':');
                if (parts.length === 2) {
                    const hours = parseInt(parts[0], 10);
                    const minutes = parseInt(parts[1], 10);

                    if (hours === 24 && minutes > 0) {
                        return '24:00';
                    }
                }

                return value;
            }}
            validate={(value: string) => {
                const parts = value.split(':');
                if (parts.length === 2) {
                    const hours = parseInt(parts[0], 10);
                    const minutes = parseInt(parts[1], 10);

                    if (hours === 24 && minutes > 0) {
                        return false;
                    }
                }

                return true;
            }}
            placeholder={'24:00'}
            inputRef={ref}
            onAccept={(value: string) => props.onChange({ target: { name: props.name, value } })}
            overwrite
        />
    );
});

const TextMaskCustomMax99 = forwardRef<HTMLInputElement, any>((props, ref) => {
    return (
        <IMaskInput
            {...props}
            mask="HH:MM"
            definitions={{
                H: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 2,
                },
                M: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 5,
                },
            }}
            blocks={{
                HH: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 99,
                    maxLength: 2,
                },
                MM: {
                    mask: IMask.MaskedRange,
                    from: 0,
                    to: 59,
                    maxLength: 2,
                },
            }}
            prepare={(value: string) => {
                const parts = value.split(':');
                if (parts.length === 2) {
                    const hours = parseInt(parts[0], 10);
                    const minutes = parseInt(parts[1], 10);

                    if (hours === 99 && minutes > 0) {
                        return '99:00';
                    }
                }

                return value;
            }}
            validate={(value: string) => {
                const parts = value.split(':');
                if (parts.length === 2) {
                    const hours = parseInt(parts[0], 10);
                    const minutes = parseInt(parts[1], 10);

                    if (hours === 99 && minutes > 0) {
                        return false;
                    }
                }

                return true;
            }}
            placeholder={'99:00'}
            inputRef={ref}
            onAccept={(value: string) => props.onChange({ target: { name: props.name, value } })}
            overwrite
        />
    );
});

const ExceptionRuleFormDialog = ({
    open,
    mode,
    loading,
    values,
    paycodeOptions,
    inPaycodesDuration,
    outPaycodesDuration,
    nameExists,
    nameExistsTextKey,
    validateInPunchVeryEarly,
    validateOutPunchVeryLate,
    timeValidation,
    submitDisabled,
    showUnsavedAlert,
    onNameChange,
    onDescriptionChange,
    onNotPlanningChange,
    onTimeFieldChange,
    onInPaycodeChange,
    onOutPaycodeChange,
    onRequestClose,
    onDismissUnsavedAlert,
    onDiscardChanges,
    onSubmit,
}: ExceptionRuleFormDialogProps) => {
    const { t } = useTranslation();

    const baseKey = `settings.exception_rules.table.dialog.${mode}`;

    return (
        <Dialog
            open={open}
            PaperComponent={PaperComponent}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                onRequestClose();
            }}
            maxWidth="sm"
            fullWidth
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t(`${baseKey}.title`)}
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={onRequestClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {showUnsavedAlert && (
                    <Alert
                        variant="filled"
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={onDismissUnsavedAlert} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>
                                <Button onClick={onDiscardChanges} color="inherit" size="small" variant="text" disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <TextField
                            id="name"
                            name="name"
                            fullWidth
                            label={t(`${baseKey}.name`)}
                            value={values.name}
                            autoComplete="off"
                            disabled={loading}
                            onChange={(e) => {
                                const newName = e.target.value;
                                if (nameValidation(newName)) {
                                    onNameChange(newName);
                                }
                            }}
                            required
                            error={nameExists}
                            slotProps={{
                                htmlInput: { maxLength: 100 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${values.name.length}/100`}</InputAdornment>,
                                },
                            }}
                        />
                        <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }}>
                            <span>{nameExists ? t(nameExistsTextKey) : ''}</span>
                        </FormHelperText>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1.5 }}>
                        <FormControlLabel
                            disabled={loading}
                            control={<Checkbox checked={values.not_planning} onChange={(e) => onNotPlanningChange(e.target.checked)} />}
                            label={t(`${baseKey}.not_planning`)}
                        />
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1.5 }}>
                        <FormControl>
                            <TextField
                                label={t(`${baseKey}.long_interval`)}
                                value={values.long_interval}
                                autoComplete="off"
                                disabled={loading}
                                name="long_interval"
                                id="long_interval-mask-input"
                                slotProps={{
                                    input: {
                                        inputComponent: TextMaskCustomMax99 as any,
                                        endAdornment: timeValidation(values.long_interval) && (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'El formato es HH:mm'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                onChange={(e) => onTimeFieldChange('long_interval', e.target.value)}
                                error={timeValidation(values.long_interval)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1.5 }}>
                        <FormControl>
                            <TextField
                                label={t(`${baseKey}.shortened_shift`)}
                                value={values.shortened_shift}
                                name="shortened_shift"
                                autoComplete="off"
                                disabled={loading}
                                id="shortened_shift-mask-input"
                                slotProps={{
                                    input: {
                                        inputComponent: TextMaskCustom as any,
                                        endAdornment: timeValidation(values.shortened_shift) && (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'El formato es HH:mm'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                onChange={(e) => onTimeFieldChange('shortened_shift', e.target.value)}
                                error={timeValidation(values.shortened_shift)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <Typography variant="subtitle1">{t(`${baseKey}.punch_in`)}</Typography>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1 }}>
                        <FormControl>
                            <TextField
                                label={t(`${baseKey}.in_punch_very_early`)}
                                name="in_punch_very_early"
                                id="in_punch_very_early-mask-input"
                                autoComplete="off"
                                value={values.in_punch_very_early}
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        inputComponent: TextMaskCustom as any,
                                        endAdornment: timeValidation(values.in_punch_very_early) ? (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'El formato es HH:mm'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                                onChange={(e) => onTimeFieldChange('in_punch_very_early', e.target.value)}
                                error={timeValidation(values.in_punch_very_early)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1 }}>
                        <FormControl>
                            <TextField
                                label={t(`${baseKey}.in_punch_early`)}
                                name="in_punch_early"
                                id="in_punch_early-mask-input"
                                value={values.in_punch_early}
                                disabled={loading}
                                autoComplete="off"
                                slotProps={{
                                    input: {
                                        inputComponent: TextMaskCustom as any,
                                        endAdornment: validateInPunchVeryEarly(values.in_punch_early) ? (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'La hora de entrada temprana tiene que ser menor a la hora de entrada muy temprana'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : timeValidation(values.in_punch_early) ? (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'El formato es HH:mm'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                                onChange={(e) => onTimeFieldChange('in_punch_early', e.target.value)}
                                error={validateInPunchVeryEarly(values.in_punch_early) || timeValidation(values.in_punch_early)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1 }}>
                        <FormControl>
                            <TextField
                                label={t(`${baseKey}.in_punch_late`)}
                                name="in_punch_late"
                                id="in_punch_late-mask-input"
                                autoComplete="off"
                                value={values.in_punch_late}
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        inputComponent: TextMaskCustom as any,
                                        endAdornment: timeValidation(values.in_punch_late) ? (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'El formato es HH:mm'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                                onChange={(e) => onTimeFieldChange('in_punch_late', e.target.value)}
                                error={timeValidation(values.in_punch_late)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Autocomplete<PaycodeOption, false, false, false>
                            options={paycodeOptions}
                            value={inPaycodesDuration}
                            getOptionLabel={(option) => (option?.name ? option.name : '')}
                            onChange={(_, newValue) => onInPaycodeChange(newValue)}
                            renderOption={(props, option) => <li {...props} key={`in_${option.paycode_id}`}>{option.name}</li>}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoComplete="off"
                                    label={t(`${baseKey}.select_paycodes_hours`)}
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        },
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.paycode_id === value.paycode_id}
                            noOptionsText={t('general.autocomplete.noResults')}
                            loadingText={t('general.autocomplete.loading')}
                            disabled={loading}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <Typography variant="subtitle1">{t(`${baseKey}.punch_out`)}</Typography>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1 }}>
                        <FormControl>
                            <TextField
                                label={t(`${baseKey}.out_punch_early`)}
                                name="out_punch_early"
                                id="out_punch_early-mask-input"
                                autoComplete="off"
                                value={values.out_punch_early}
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        inputComponent: TextMaskCustom as any,
                                        endAdornment: timeValidation(values.out_punch_early) ? (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'El formato es HH:mm'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                                onChange={(e) => onTimeFieldChange('out_punch_early', e.target.value)}
                                error={timeValidation(values.out_punch_early)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1 }}>
                        <FormControl>
                            <TextField
                                label={t(`${baseKey}.out_punch_late`)}
                                name="out_punch_late"
                                id="out_punch_late-mask-input"
                                autoComplete="off"
                                value={values.out_punch_late}
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        inputComponent: TextMaskCustom as any,
                                        endAdornment: validateOutPunchVeryLate(values.out_punch_late) ? (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'La hora de salida tarde tiene que ser menor a la hora de salida muy tarde'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : timeValidation(values.out_punch_late) ? (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'El formato es HH:mm'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                                onChange={(e) => onTimeFieldChange('out_punch_late', e.target.value)}
                                error={validateOutPunchVeryLate(values.out_punch_late) || timeValidation(values.out_punch_late)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }} sx={{ mt: 1 }}>
                        <FormControl>
                            <TextField
                                label={t(`${baseKey}.out_punch_very_late`)}
                                name="out_punch_very_late"
                                id="out_punch_very_late-mask-input"
                                autoComplete="off"
                                value={values.out_punch_very_late}
                                disabled={loading}
                                slotProps={{
                                    input: {
                                        inputComponent: TextMaskCustom as any,
                                        endAdornment: timeValidation(values.out_punch_very_late) ? (
                                            <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                                                <Tooltip title={'El formato es HH:mm'} arrow>
                                                    <InfoIcon color="error" />
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                                onChange={(e) => onTimeFieldChange('out_punch_very_late', e.target.value)}
                                error={timeValidation(values.out_punch_very_late)}
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Autocomplete<PaycodeOption, false, false, false>
                            options={paycodeOptions}
                            value={outPaycodesDuration}
                            onChange={(_, newValue) => onOutPaycodeChange(newValue)}
                            getOptionLabel={(option) => (option?.name ? option.name : '')}
                            renderOption={(props, option) => <li {...props} key={`out_${option.paycode_id}`}>{option.name}</li>}
                            isOptionEqualToValue={(option, value) => option.paycode_id === value.paycode_id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoComplete="off"
                                    label={t(`${baseKey}.select_paycodes_hours`)}
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        },
                                    }}
                                />
                            )}
                            noOptionsText={t('general.autocomplete.noResults')}
                            loadingText={t('general.autocomplete.loading')}
                            disabled={loading}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <TextField
                            id="description"
                            fullWidth
                            placeholder={t(`${baseKey}.max_description`)}
                            label={t(`${baseKey}.description`)}
                            value={values.description}
                            disabled={loading}
                            multiline
                            rows={2}
                            autoComplete="off"
                            onChange={(e) => onDescriptionChange(e.target.value)}
                            slotProps={{
                                htmlInput: { maxLength: 255 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${values.description.length}/255`}</InputAdornment>,
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onRequestClose} variant="contained" color="error">
                    {mode === 'edit' ? t(`${baseKey}.cancel`) : t(`${baseKey}.clean`)}
                </Button>
                <Button disabled={submitDisabled} loading={loading} onClick={onSubmit} variant="contained" color="primary">
                    {mode === 'edit' ? t(`${baseKey}.edit`) : t(`${baseKey}.add`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export type { ExceptionRuleFormValues, PaycodeOption };
export { ExceptionRuleFormDialog };
